import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { FileSpecificationService } from 'src/app/services/file-specifications/file-specification.service';
import { FileSpecification } from 'src/app/models/file-specifications/file-specification';
import { FileSpecificationVideosSettings } from 'src/app/models/file-specifications/file-specification-settings';
import {
  OutputVideoFormat,
  ALL_OUTPUT_VIDEO_FORMATS,
} from 'src/app/models/file-specifications/output-video-format';
import { VideoBitRate } from 'src/app/models/file-specifications/video-bit-rate';

export interface VideosFileSpecificationCreateDialogData {
  organizationId: string;
}

export type BitRateMode = VideoBitRate['mode'];

const ALL_BIT_RATE_MODES: readonly BitRateMode[] = ['auto', 'fixed'] as const;

const BIT_RATE_MODE_LABELS: Record<BitRateMode, string> = {
  auto: 'Auto',
  fixed: 'Fixed Mbps',
};

const VIDEO_FORMAT_LABELS: Record<OutputVideoFormat, string> = {
  Mp4H264: 'MP4 H.264',
  Mp4H265: 'MP4 H.265',
  MovH264: 'MOV H.264',
  MovH265: 'MOV H.265',
  WebM: 'WebM',
  Gif: 'GIF',
};

@Component({
  selector: 'app-videos-file-specification-create-dialog',
  templateUrl: './videos-file-specification-create-dialog.component.html',
  styleUrls: ['./videos-file-specification-create-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class VideosFileSpecificationCreateDialogComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly videoFormats = ALL_OUTPUT_VIDEO_FORMATS;
  readonly bitRateModes = ALL_BIT_RATE_MODES;
  readonly formatLabel = (f: OutputVideoFormat): string => VIDEO_FORMAT_LABELS[f];
  readonly bitRateModeLabel = (m: BitRateMode): string => BIT_RATE_MODE_LABELS[m];

  readonly nameControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(1)],
  });

  readonly formatControl = new FormControl<OutputVideoFormat>('Mp4H264', {
    nonNullable: true,
    validators: [Validators.required],
  });

  readonly widthControl = new FormControl<number>(1920, {
    nonNullable: true,
    validators: [Validators.required, Validators.min(1)],
  });

  readonly heightControl = new FormControl<number>(1080, {
    nonNullable: true,
    validators: [Validators.required, Validators.min(1)],
  });

  readonly bitRateModeControl = new FormControl<BitRateMode>('auto', {
    nonNullable: true,
    validators: [Validators.required],
  });

  readonly mbpsControl = new FormControl<number>(8, {
    nonNullable: true,
    validators: [Validators.required, Validators.min(1)],
  });

  submitting = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: VideosFileSpecificationCreateDialogData,
    private readonly dialogRef: MatDialogRef<
      VideosFileSpecificationCreateDialogComponent,
      FileSpecification | undefined
    >,
    private readonly service: FileSpecificationService,
  ) {}

  get isFixedBitRate(): boolean {
    return this.bitRateModeControl.value === 'fixed';
  }

  get canSubmit(): boolean {
    if (this.submitting) return false;
    if (!this.nameControl.valid) return false;
    if (!this.formatControl.valid) return false;
    if (!this.widthControl.valid) return false;
    if (!this.heightControl.valid) return false;
    if (!this.bitRateModeControl.valid) return false;
    if (this.isFixedBitRate && !this.mbpsControl.valid) return false;
    return true;
  }

  submit(): void {
    if (!this.canSubmit) return;
    this.submitting = true;
    this.service
      .create(this.data.organizationId, {
        name: this.nameControl.value.trim(),
        value: { settings: this.buildSettings() },
        writeReason: { kind: 'initialCreate' },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: FileSpecification) => this.dialogRef.close(result),
        error: () => {
          this.submitting = false;
        },
      });
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  private buildSettings(): FileSpecificationVideosSettings {
    return {
      media: 'videos',
      format: this.formatControl.value,
      width: this.widthControl.value,
      height: this.heightControl.value,
      bitRate: this.buildBitRate(),
    };
  }

  private buildBitRate(): VideoBitRate {
    if (this.bitRateModeControl.value === 'auto') return { mode: 'auto' };
    return { mode: 'fixed', mbps: this.mbpsControl.value };
  }
}
